<template>
    <!-- Wrapping div, not the bare canvas, is this component's root: fabric.Canvas() (see
         mounted() below) moves the canvas into its own ".canvas-container" wrapper the instant
         it's constructed, so a bare <canvas> root would leave Vue tracking a DOM node that's no
         longer a direct child of whatever parent it was originally rendered into. That mismatch
         only bites when a sibling element (e.g. CropSelector) is mounted/unmounted right next to
         this component afterward - Vue's patch tries to insertBefore/removeChild relative to a
         reference node fabric already relocated, throwing "NotFoundError: ... is not a child of
         this node" and silently breaking that patch (later reactive updates keep computing
         correctly but stop reaching the DOM). A stable div root keeps fabric's own DOM surgery
         fully contained one level down, invisible to this component's parent.
    -->
    <div class="overlayCanvas">
        <canvas ref="canvas"></canvas>
    </div>
</template>

<script>
import * as fabric from 'fabric'
import { decodeGifFrames, getGifFrameAt } from '../engine/widgets/widgetRenderers.js'
import { createFabricObjectForOverlay, hasIconContent, setObjectBlur } from '../engine/compositor/overlayRenderer.js'
import { computeOverlayAnimationTransform } from '../engine/compositor/overlayAnimation.js'
import { computeSnap } from '../engine/snapGuides.js'

const GIF_TICK_MS = 100

export default {
    name: 'OverlayCanvas',

    props: {
        overlays: {
            type: Array,
            default: () => []
        },
        canvasSize: {
            type: Object,
            required: true
        },
        currentMs: {
            type: Number,
            default: 0
        }
    },

    data() {
        return {
            fabricCanvas: null,
            objectsByOverlayId: new Map(),
            contentSignatureByOverlayId: new Map(),
            gifDataByOverlayId: new Map(),
            gifStartedAt: new Map(),
            gifInterval: null,
            guideLineX: null,
            guideLineY: null,
            suppressSelectionEvents: false,
            lastTransformType: null,
            // Baseline captured at the start of a group drag - see onObjectMoving/onObjectModified.
            groupDragStart: null,
            // One persistent, clickable dashed rect per group (groupId -> fabric.Rect) - see
            // syncGroupFrames. Clicking it selects the whole group; clicking a member only selects
            // that member (see onSelectionChanged). Visible while selected or hovered - see
            // updateGroupFrameVisibility.
            groupFrameByGroupId: new Map(),
            hoveredGroupId: null,
            // Single reusable hover indicator for a standalone (non-grouped) overlay - purely
            // decorative, repositioned as hover moves rather than being its own click target.
            hoverHighlight: null
        }
    },

    watch: {
        overlays: {
            deep: true,
            handler() {
                this.syncOverlays()
            }
        },
        currentMs() {
            this.updateVisibility()
        },
        // The crop/ratio tools can change the export canvas size after mount (canvasSize is
        // derived from timeline.canvas.width/height) - the fabric backstore must track it so
        // xPct/yPct math stays correct, without touching the CSS size (kept at 100%/100%, see below).
        canvasSize: {
            deep: true,
            handler(size) {
                if (!this.fabricCanvas) return
                this.fabricCanvas.setDimensions({ width: size.width, height: size.height }, { backstoreOnly: true })
                this.syncOverlays()
            }
        }
    },

    mounted() {
        this.fabricCanvas = new fabric.Canvas(this.$refs.canvas, {
            width: this.canvasSize.width,
            height: this.canvasSize.height,
            backgroundColor: 'transparent',
            selection: true,
            // Fabric's own default is Shift only - Ctrl/Cmd is what users actually expect for
            // add/remove-from-selection.
            selectionKey: ['ctrlKey', 'metaKey', 'shiftKey']
        })
        // Fabric sizes its own canvases with hardcoded pixel CSS dimensions (matching the
        // backstore resolution) by default - at the popup's scaled-down display size, that left
        // the interactive layer oversized/misaligned vs. the video underneath. Forcing CSS-only
        // 100%/100% keeps the backstore at full resolution while fitting the stage like the plain
        // compositing <canvas> does.
        this.fabricCanvas.setDimensions({ width: '100%', height: '100%' }, { cssOnly: true })
        this.fabricCanvas.on('object:modified', this.onObjectModified)
        this.fabricCanvas.on('object:moving', this.onObjectMoving)
        // lastTransformType records which gesture is in progress - onObjectModified reads it to
        // tell an actual resize apart from a plain move/rotate (fabric's own e.transform.action
        // string isn't reliable enough across versions for that).
        this.fabricCanvas.on('object:scaling', () => {
            this.lastTransformType = 'scale'
            this.emitLiveTransform()
        })
        this.fabricCanvas.on('object:rotating', () => {
            this.lastTransformType = 'rotate'
            this.emitLiveTransform()
        })
        this.fabricCanvas.on('mouse:up', this.hideGuideLines)
        this.fabricCanvas.on('mouse:dblclick', this.onCanvasDoubleClick)
        this.fabricCanvas.on('mouse:over', (e) => this.onObjectHover(e.target))
        this.fabricCanvas.on('mouse:out', this.onObjectUnhover)
        this.fabricCanvas.on('selection:created', this.onSelectionChanged)
        this.fabricCanvas.on('selection:updated', this.onSelectionChanged)
        this.fabricCanvas.on('selection:cleared', () => {
            if (this.suppressSelectionEvents) return
            this.$emit('select', null)
            this.$emit('multi-selection', null)
            this.forceSelectionRepaint()
            this.updateGroupFrameVisibility()
        })
        this.syncOverlays()
        this.gifInterval = setInterval(this.tickGifs, GIF_TICK_MS)

        // Fabric only recalculates its click-to-canvas offset (calcOffset) on the window's own
        // 'resize' event - not when this element's own size/position changes for any other reason
        // (e.g. the popup's "Agrandir/Réduire" toggle), which otherwise leaves clicks mapped to a
        // stale position.
        this.resizeObserver = new ResizeObserver(() => {
            if (this.fabricCanvas) this.fabricCanvas.calcOffset()
        })
        this.resizeObserver.observe(this.$refs.canvas.parentElement)
    },

    beforeDestroy() {
        if (this.gifInterval) clearInterval(this.gifInterval)
        if (this.resizeObserver) this.resizeObserver.disconnect()
        if (this.fabricCanvas) {
            try {
                this.fabricCanvas.dispose()
            } catch (error) {
                console.error(error)
            }
        }
    },

    methods: {
        getCanvasElement() {
            return this.fabricCanvas ? this.fabricCanvas.lowerCanvasEl : null
        },

        // Fabric sets _groupSelector at the very start of a rubber-band drag and _currentTransform
        // at the start of a move/scale/rotate, clearing both only on mouseup - a reliable, live
        // signal for "the user's mouse button is still down on this canvas right now" (see
        // MediaEditor.vue's selectedOverlayId watcher, which needs to know this before echoing a
        // deselect back in).
        hasActiveGesture() {
            return !!(this.fabricCanvas && (this.fabricCanvas._groupSelector || this.fabricCanvas._currentTransform))
        },

        // selection:created/updated/cleared fire *synchronously*, mid-way through fabric's own
        // mousedown dispatch - rendering back out reentrantly at that exact moment isn't something
        // fabric's own subsequent code in that handler expects. Deferring to the next animation
        // frame runs this after fabric's own dispatch has fully unwound.
        forceSelectionRepaint() {
            requestAnimationFrame(() => {
                if (!this.fabricCanvas) return
                this.fabricCanvas.renderAll()
                this.fabricCanvas.renderTop()
            })
        },

        selectOverlay(overlayId) {
            this.selectOverlays(overlayId ? [overlayId] : [])
        },

        // 2+ ids -> a real fabric.ActiveSelection, which gives native group dragging for free.
        selectOverlays(overlayIds) {
            if (!this.fabricCanvas) return
            const objs = overlayIds.map(id => this.objectsByOverlayId.get(id)).filter(Boolean)
            if (!objs.length) {
                // renderTop() alongside renderAll() - the selection border lives on its own canvas
                // layer (contextTop), which discardActiveObject() doesn't always get repainted.
                this.fabricCanvas.discardActiveObject()
                this.fabricCanvas.renderAll()
                this.fabricCanvas.renderTop()
                this.updateGroupFrameVisibility()
                return
            }
            if (objs.length === 1) {
                if (this.fabricCanvas.getActiveObject() !== objs[0]) this.fabricCanvas.setActiveObject(objs[0])
            } else {
                // No resize/rotate handles for any multi-selection (only translation is
                // supported collectively) - and no solid border either for a complete group,
                // whose own persistent dashed frame (see syncGroupFrames) is the sole indicator.
                const { isCompleteGroup } = this.isCompleteGroupSelection(overlayIds)
                this.fabricCanvas.setActiveObject(new fabric.ActiveSelection(objs, {
                    canvas: this.fabricCanvas,
                    hasControls: false,
                    hasBorders: !isCompleteGroup
                }))
            }
            this.fabricCanvas.renderAll()
            this.fabricCanvas.renderTop()
            this.updateGroupFrameVisibility()
        },

        // Synchronous read for MediaEditor.vue's keyboard shortcuts (Ctrl+A/Ctrl+G/Delete...).
        getSelectedOverlayIds() {
            const active = this.fabricCanvas && this.fabricCanvas.getActiveObject()
            if (!active) return []
            if (active instanceof fabric.ActiveSelection) return active.getObjects().map(o => o.data && o.data.overlayId).filter(Boolean)
            return active.data ? [active.data.overlayId] : []
        },

        // Anything that changes what the object actually LOOKS like (text, colors, font, size,
        // widget-specific fields...) - as opposed to position/rotation, which fabric itself
        // already reflects live while dragging/resizing.
        //
        // Text with a prefix/suffix icon is the one exception: its width slider changes
        // overlay.transform.widthPct, which normally only goes through the cheap
        // syncObjectTransform path (a plain resize+re-wrap) - but a Textbox nested inside an
        // icon-layout Group needs the icon(s) repositioned and the group's own bounds
        // recalculated too, which that path doesn't know how to do. Folding widthPct into the
        // signature for these routes every width change through the same full rebuild used at
        // creation, reusing overlayRenderer.js's layout logic instead of duplicating it here.
        contentSignature(overlay) {
            return JSON.stringify({
                type: overlay.type,
                widgetType: overlay.widgetType,
                data: overlay.data,
                iconWidthPct: hasIconContent(overlay) ? overlay.transform.widthPct : undefined
            })
        },

        removeOverlayObject(id) {
            const existing = this.objectsByOverlayId.get(id)
            if (existing) this.fabricCanvas.remove(existing)
            this.objectsByOverlayId.delete(id)
            this.contentSignatureByOverlayId.delete(id)
            this.gifDataByOverlayId.delete(id)
            this.gifStartedAt.delete(id)
        },

        // Async (image overlays need to actually load their file) - a fresh sync can already be
        // running by the time this resolves (overlay removed, or rebuilt again from a rapid data
        // change), so it re-checks membership before touching the canvas/maps.
        async createOverlayObject(overlay) {
            const obj = await createFabricObjectForOverlay(overlay, this.canvasSize)
            if (!obj || !this.fabricCanvas) return
            if (!this.overlays.some(o => o.id === overlay.id)) return

            // A second sync can have already built and registered an object for this same
            // overlay while this one was still awaiting (e.g. two rapid data changes) - without
            // this, the earlier object stays added to the canvas forever with nothing left
            // pointing at it to remove it, showing as a permanent duplicate at its old position/
            // content next to the one actually being kept in sync from here on.
            const existing = this.objectsByOverlayId.get(overlay.id)
            if (existing) this.fabricCanvas.remove(existing)

            this.objectsByOverlayId.set(overlay.id, obj)
            this.contentSignatureByOverlayId.set(overlay.id, this.contentSignature(overlay))
            this.gifStartedAt.set(overlay.id, Date.now())
            this.fabricCanvas.add(obj)
            this.enforceStackingOrder()
            this.updateVisibility()
            this.fabricCanvas.renderAll()

            if (overlay.widgetType === 'gif' && overlay.data && overlay.data.gifUrl) {
                decodeGifFrames(overlay.data.gifUrl).then((gifData) => {
                    this.gifDataByOverlayId.set(overlay.id, gifData)
                }).catch(() => {})
            }
        },

        syncOverlays() {
            if (!this.fabricCanvas) return

            const currentIds = new Set(this.overlays.map(o => o.id))
            ;[...this.objectsByOverlayId.keys()].forEach((id) => {
                if (!currentIds.has(id)) this.removeOverlayObject(id)
            })

            this.overlays.forEach((overlay) => {
                if (!this.objectsByOverlayId.has(overlay.id)) {
                    this.createOverlayObject(overlay)
                    return
                }

                const signature = this.contentSignature(overlay)
                if (this.contentSignatureByOverlayId.get(overlay.id) !== signature) {
                    // Content actually changed - can't be patched generically across overlay
                    // types, so rebuild in place. suppressSelectionEvents hides the momentary
                    // remove+re-add's selection:cleared/created from the rest of the app (it would
                    // otherwise unmount OverlayConfigPanel for a rebuild that isn't a real
                    // deselect) - .catch()+.finally() guarantees it gets released even if
                    // createOverlayObject throws.
                    const wasSelected = this.fabricCanvas.getActiveObject() === this.objectsByOverlayId.get(overlay.id)
                    if (wasSelected) this.suppressSelectionEvents = true
                    this.removeOverlayObject(overlay.id)
                    this.createOverlayObject(overlay)
                        .then(() => { if (wasSelected) this.selectOverlay(overlay.id) })
                        .catch(() => {})
                        .finally(() => { if (wasSelected) this.suppressSelectionEvents = false })
                } else {
                    // Position/rotation/scale may have changed from the config panel (as opposed
                    // to a canvas drag, which fabric already reflects live) - apply those too.
                    this.syncObjectTransform(this.objectsByOverlayId.get(overlay.id), overlay)
                }
            })

            this.enforceStackingOrder()
            this.syncGroupFrames()
            this.updateVisibility()
            this.fabricCanvas.renderAll()
            // renderAll() never touches contextTop (the selection border's own layer) - a sync
            // that repositions/reorders objects can leave a stale border there otherwise.
            this.fabricCanvas.renderTop()
        },

        // The overlays array order *is* the layering order (front = last) - "avancer"/"reculer"
        // reorder that array (see MediaEditor.vue's reorderOverlay), and this is what makes
        // fabric's own paint order actually follow it, since adding/patching objects doesn't
        // otherwise change where they already sit in fabric's internal stacking.
        enforceStackingOrder() {
            this.overlays.forEach((overlay) => {
                const obj = this.objectsByOverlayId.get(overlay.id)
                if (obj) this.fabricCanvas.bringObjectToFront(obj)
            })
        },

        // Applies transform.xPct/yPct/rotationDeg/scale from Vue state onto the fabric object,
        // but only when it actually differs (within a small epsilon) from what fabric already
        // has - this is what lets a config-panel edit reach the canvas without fighting an
        // in-progress drag (whose own end-of-drag update already matches almost exactly).
        syncObjectTransform(obj, overlay) {
            if (!obj) return
            // Still part of an active multi-selection - its left/top mean "relative to that
            // selection" for as long as it lasts, not canvas-absolute, so writing xPct/yPct
            // straight onto them here would corrupt its position. Fabric's own live rendering
            // already shows it correctly; this catches up once the user eventually deselects.
            if (obj.group) return
            const targetLeft = overlay.transform.xPct * this.canvasSize.width
            const targetTop = overlay.transform.yPct * this.canvasSize.height
            const targetAngle = overlay.transform.rotationDeg || 0
            const isImage = overlay.type === 'image'

            // Images size themselves via independent width/height percentages (kept in sync with
            // scaleX/scaleY at creation - see overlayRenderer.js) rather than the generic
            // "Échelle" multiplier every other overlay type uses, so the exact width/height
            // entered in the panel map directly onto the rendered image instead of being a
            // multiplier of whatever size it happened to start at. When the aspect ratio is
            // locked, X and Y still end up equal (both derived from widthPct via the image's own
            // ratio) - only an *unlocked* image can end up with scaleX !== scaleY.
            let targetScaleX
            let targetScaleY
            if (isImage) {
                const lockRatio = overlay.transform.lockAspectRatio !== false
                targetScaleX = overlay.transform.widthPct ? (overlay.transform.widthPct * this.canvasSize.width) / obj.width : (obj.scaleX || 1)
                targetScaleY = lockRatio
                    ? targetScaleX * (obj.width / obj.height) // keep the image's own native ratio
                    : (overlay.transform.heightPct ? (overlay.transform.heightPct * this.canvasSize.height) / obj.height : (obj.scaleY || 1))
            } else {
                const targetScale = overlay.transform.scale || 1
                targetScaleX = targetScale
                targetScaleY = targetScale
            }

            // A Textbox (plain, or nested inside a chrome Group - see wrapTextWithChrome) is the
            // only shape whose "Largeur de la zone" width slider does anything. Must check
            // `instanceof fabric.Textbox` specifically, not a duck-typed check like
            // `initDimensions` - that's also defined on plain fabric.Text, which is what a widget
            // pill's own label actually is (createWidgetPill uses FabricText), and would wrongly
            // resize the widget's pill using text-chrome padding instead of its own.
            const textbox = obj instanceof fabric.Textbox
                ? obj
                : (obj._objects && obj._objects.find(o => o instanceof fabric.Textbox))
            const targetWidth = textbox && overlay.transform.widthPct
                ? overlay.transform.widthPct * this.canvasSize.width
                : (textbox ? textbox.width : null)

            const needsUpdate = Math.abs(obj.left - targetLeft) > 0.5
                || Math.abs(obj.top - targetTop) > 0.5
                || Math.abs((obj.angle || 0) - targetAngle) > 0.5
                || Math.abs((obj.scaleX || 1) - targetScaleX) > 0.01
                || Math.abs((obj.scaleY || 1) - targetScaleY) > 0.01
                || (textbox && Math.abs(textbox.width - targetWidth) > 0.5)

            // Captured unconditionally so updateVisibility always has a base to animate on top of.
            obj.__baseLeft = targetLeft
            obj.__baseTop = targetTop
            obj.__baseScaleX = targetScaleX
            obj.__baseScaleY = targetScaleY
            obj.__baseAngle = targetAngle

            if (needsUpdate) {
                obj.set({ left: targetLeft, top: targetTop, angle: targetAngle, scaleX: targetScaleX, scaleY: targetScaleY })

                if (isImage && typeof obj.setControlsVisibility === 'function') {
                    const lockRatio = overlay.transform.lockAspectRatio !== false
                    obj.setControlsVisibility({ ml: !lockRatio, mr: !lockRatio, mt: !lockRatio, mb: !lockRatio })
                }

                if (textbox) {
                    // A Textbox doesn't re-wrap its text just because .width was set (that only
                    // happens automatically on construction or an interactive side-handle resize) -
                    // without this, the "Largeur de la zone" slider changed the value but nothing
                    // ever appeared to happen on the actual video.
                    textbox.set({ width: targetWidth })
                    textbox.initDimensions()

                    if (textbox !== obj) {
                        // Chrome-wrapped text - the backing rect was sized to fit the text at
                        // creation time and needs to follow it as it re-wraps to the new width.
                        // Same per-side padding/offset math as overlayRenderer.js's
                        // wrapTextWithChrome, kept in sync here for the live-resize path.
                        const bg = obj._objects.find(o => o !== textbox)
                        if (bg) {
                            const fontSize = textbox.fontSize || 42
                            const data = overlay.data
                            const paddingTop = data.paddingTop !== undefined ? data.paddingTop : Math.max(6, fontSize * 0.25)
                            const paddingRight = data.paddingRight !== undefined ? data.paddingRight : Math.max(8, fontSize * 0.3)
                            const paddingBottom = data.paddingBottom !== undefined ? data.paddingBottom : Math.max(6, fontSize * 0.25)
                            const paddingLeft = data.paddingLeft !== undefined ? data.paddingLeft : Math.max(8, fontSize * 0.3)
                            bg.set({
                                width: textbox.width + paddingLeft + paddingRight,
                                height: textbox.height + paddingTop + paddingBottom,
                                left: (paddingRight - paddingLeft) / 2,
                                top: (paddingBottom - paddingTop) / 2
                            })
                        }
                    }
                }

                obj.setCoords()
            }
        },

        // Position/scale/rotation only touched during an active animation window (isAnimatingTransform) -
        // otherwise this would fight syncObjectTransform's own diffing or an in-progress drag.
        updateVisibility() {
            if (!this.fabricCanvas) return
            this.overlays.forEach((overlay) => {
                const obj = this.objectsByOverlayId.get(overlay.id)
                if (!obj) return
                const active = this.currentMs >= overlay.startMs && this.currentMs <= overlay.endMs
                obj.visible = active
                if (!active) return

                const anim = computeOverlayAnimationTransform(overlay, this.currentMs)
                obj.opacity = anim.opacity

                const isAnimatingTransform = anim.offsetXPct !== 0 || anim.offsetYPct !== 0 || anim.scale !== 1 || anim.rotationDeg !== 0
                if (isAnimatingTransform) {
                    const baseLeft = obj.__baseLeft !== undefined ? obj.__baseLeft : obj.left
                    const baseTop = obj.__baseTop !== undefined ? obj.__baseTop : obj.top
                    const baseScaleX = obj.__baseScaleX !== undefined ? obj.__baseScaleX : (obj.scaleX || 1)
                    const baseScaleY = obj.__baseScaleY !== undefined ? obj.__baseScaleY : (obj.scaleY || 1)
                    const baseAngle = obj.__baseAngle !== undefined ? obj.__baseAngle : (obj.angle || 0)
                    obj.set({
                        left: baseLeft + anim.offsetXPct * this.canvasSize.width,
                        top: baseTop + anim.offsetYPct * this.canvasSize.height,
                        scaleX: baseScaleX * anim.scale,
                        scaleY: baseScaleY * anim.scale,
                        angle: baseAngle + anim.rotationDeg
                    })
                    obj.setCoords()
                }
                setObjectBlur(obj, anim.blurPx)
            })
            this.fabricCanvas.renderAll()
            this.fabricCanvas.renderTop()
        },

        tickGifs() {
            if (!this.fabricCanvas || !this.gifDataByOverlayId.size) return
            let changed = false
            this.gifDataByOverlayId.forEach((gifData, overlayId) => {
                const obj = this.objectsByOverlayId.get(overlayId)
                if (!obj || !obj.visible) return
                const startedAt = this.gifStartedAt.get(overlayId) || Date.now()
                const frameCanvas = getGifFrameAt(gifData, Date.now() - startedAt)
                if (frameCanvas) {
                    obj.setElement(frameCanvas)
                    changed = true
                }
            })
            if (changed) this.fabricCanvas.requestRenderAll()
        },

        // Blue alignment guides (Story Maker's draggable.vue axeX/axeY, extended with quarter
        // marks and snapping to other overlays visible at the same moment) - snaps the object
        // being dragged and shows a full-length line at the matched position.
        onObjectMoving(e) {
            const obj = e.target
            if (!obj) return

            // Fabric already moves the whole selection together natively - just keep the live
            // preview in sync, no snapping across a group. Captures a baseline on the first tick
            // of a new gesture (the selection's own left/top, plus each member's already-trusted
            // xPct/yPct from the data model) so onObjectModified can compute the drag as a pure
            // percentage delta - see its own comment for why this replaced reading each member's
            // position directly off fabric.
            if (obj instanceof fabric.ActiveSelection) {
                if (!this.groupDragStart) {
                    const positions = new Map()
                    obj.getObjects().forEach((member) => {
                        if (!member.data) return
                        const overlay = this.overlays.find(o => o.id === member.data.overlayId)
                        if (overlay) positions.set(member.data.overlayId, { xPct: overlay.transform.xPct, yPct: overlay.transform.yPct })
                    })
                    this.groupDragStart = { left: obj.left, top: obj.top, positions }
                }
                this.syncGroupFrames()
                this.emitLiveTransform()
                return
            }
            this.groupDragStart = null
            if (!obj.data) return

            this.lastTransformType = 'move'

            const xPct = obj.left / this.canvasSize.width
            const yPct = obj.top / this.canvasSize.height
            const snap = computeSnap(xPct, yPct, this.overlays, this.currentMs, obj.data.overlayId)

            if (snap.x !== null) obj.left = snap.x * this.canvasSize.width
            if (snap.y !== null) obj.top = snap.y * this.canvasSize.height

            this.updateGuideLines(snap)
            // A member dragged individually within a group still needs its group's frame to
            // track it live (the frame can genuinely grow/shrink here, unlike a whole-group drag).
            if (this.overlays.find(o => o.id === obj.data.overlayId && o.groupId)) this.syncGroupFrames()
            this.emitLiveTransform()
        },

        // The video preview canvas underneath bakes in its own snapshot of this layer, only
        // refreshed on refresh()/seek()/this event - without it, a drag/resize/rotate would show a
        // frozen pre-drag snapshot underneath the interactive layer until drop.
        emitLiveTransform() {
            this.$emit('live-transform')
        },

        ensureGuideLines() {
            if (this.guideLineX && this.guideLineY) return
            this.guideLineX = new fabric.Line([0, 0, 0, 0], {
                stroke: '#2f9bff', strokeWidth: 2, selectable: false, evented: false, excludeFromExport: true, visible: false
            })
            this.guideLineY = new fabric.Line([0, 0, 0, 0], {
                stroke: '#2f9bff', strokeWidth: 2, selectable: false, evented: false, excludeFromExport: true, visible: false
            })
            this.fabricCanvas.add(this.guideLineX, this.guideLineY)
        },

        updateGuideLines(snap) {
            this.ensureGuideLines()
            const { width, height } = this.canvasSize

            this.guideLineX.set({ x1: (snap.x || 0) * width, x2: (snap.x || 0) * width, y1: 0, y2: height, visible: snap.x !== null })
            this.guideLineY.set({ x1: 0, x2: width, y1: (snap.y || 0) * height, y2: (snap.y || 0) * height, visible: snap.y !== null })
            this.fabricCanvas.bringObjectToFront(this.guideLineX)
            this.fabricCanvas.bringObjectToFront(this.guideLineY)
            this.fabricCanvas.requestRenderAll()
        },

        hideGuideLines() {
            if (!this.guideLineX) return
            this.guideLineX.visible = false
            this.guideLineY.visible = false
            // Immediate, not just requestRenderAll() - same stuck-visible symptom as the group
            // frame turned out to trace back to a deferred render never actually reaching the
            // screen in some cases.
            this.fabricCanvas.renderAll()
        },

        // Hovering a group's frame or any of its members reveals that group's frame (see
        // updateGroupFrameVisibility); hovering a standalone (non-grouped) member shows the
        // single reusable highlight rect around it instead - same idea, just without a whole
        // group to outline. Hovering anything else (empty canvas, the video itself) clears both.
        onObjectHover(target) {
            const overlay = target && target.data && target.data.overlayId
                ? this.overlays.find(o => o.id === target.data.overlayId)
                : null
            if (target && target.data && target.data.isGroupFrame) {
                this.hoveredGroupId = target.data.groupId
                this.hideHoverHighlight()
            } else if (overlay && overlay.groupId) {
                this.hoveredGroupId = overlay.groupId
                this.hideHoverHighlight()
            } else if (overlay) {
                this.hoveredGroupId = null
                this.showHoverHighlight(target)
            } else {
                this.hoveredGroupId = null
                this.hideHoverHighlight()
            }
            this.updateGroupFrameVisibility()
        },

        onObjectUnhover() {
            this.hoveredGroupId = null
            this.hideHoverHighlight()
            this.updateGroupFrameVisibility()
        },

        ensureHoverHighlight() {
            if (this.hoverHighlight) return
            this.hoverHighlight = new fabric.Rect({
                originX: 'left',
                originY: 'top',
                fill: 'transparent',
                stroke: 'transparent',
                strokeWidth: 1.5,
                strokeDashArray: [6, 4],
                // Purely decorative - never itself a click target, so it can't ever steal a click
                // meant for the object it's outlining or interfere with hit-testing at all.
                selectable: false,
                evented: false,
                excludeFromExport: true,
                data: { isHoverHighlight: true }
            })
            this.fabricCanvas.add(this.hoverHighlight)
        },

        showHoverHighlight(obj) {
            this.ensureHoverHighlight()
            obj.setCoords()
            const box = obj.getBoundingRect()
            this.hoverHighlight.set({ left: box.left, top: box.top, width: box.width, height: box.height, stroke: '#b64985' })
            this.hoverHighlight.setCoords()
            this.fabricCanvas.sendObjectToBack(this.hoverHighlight)
        },

        hideHoverHighlight() {
            if (this.hoverHighlight) this.hoverHighlight.set('stroke', 'transparent')
        },

        // A group's frame is visible while its group is the current, complete selection, or while
        // it's being hovered - always exists underneath either way (see syncGroupFrames), this
        // only toggles its stroke color.
        updateGroupFrameVisibility() {
            if (!this.fabricCanvas) return
            const active = this.fabricCanvas.getActiveObject()
            const selectedIds = active instanceof fabric.ActiveSelection
                ? active.getObjects().map(o => o.data && o.data.overlayId).filter(Boolean)
                : []
            const selectedGroupId = selectedIds.length ? this.isCompleteGroupSelection(selectedIds).groupId : null

            this.groupFrameByGroupId.forEach((frame, groupId) => {
                frame.set('stroke', (groupId === selectedGroupId || groupId === this.hoveredGroupId) ? '#b64985' : 'transparent')
            })
            this.fabricCanvas.renderAll()
            this.fabricCanvas.renderTop()
            // VideoPreviewCanvas's compositing canvas bakes in a snapshot of this whole layer,
            // refreshed only on refresh()/seek()/live-transform - never on a plain select/deselect
            // click, which is what left a stale border/frame frozen there otherwise.
            this.emitLiveTransform()
        },

        // One persistent, dashed, clickable rect per group - the only way to select a whole group
        // (see onSelectionChanged's isGroupFrame branch); clicking a member only selects that
        // member. Recomputed from each member's live bounding box so it tracks a group move or a
        // single member dragged around inside it (see onObjectMoving/syncOverlays). Visibility is
        // handled separately - see updateGroupFrameVisibility.
        syncGroupFrames() {
            if (!this.fabricCanvas) return
            const groupIds = new Set(this.overlays.filter(o => o.groupId).map(o => o.groupId))

            ;[...this.groupFrameByGroupId.keys()].forEach((groupId) => {
                if (!groupIds.has(groupId)) this.removeGroupFrame(groupId)
            })

            groupIds.forEach((groupId) => {
                const boxes = this.overlays
                    .filter(o => o.groupId === groupId)
                    .map(o => this.objectsByOverlayId.get(o.id))
                    .filter(Boolean)
                    .map((obj) => {
                        // getBoundingRect() reads cached aCoords, only refreshed by an explicit
                        // setCoords() - a plain obj.set({left, top, ...}) (e.g. syncObjectTransform)
                        // never calls it, so without this the frame was computed from wherever a
                        // member used to be, not its actual current position.
                        obj.setCoords()
                        return obj.getBoundingRect()
                    })

                // A "group" of fewer than 2 currently-resolvable members (others removed) isn't
                // worth a frame - dissolve it instead of drawing a box around a single element.
                if (boxes.length < 2) {
                    this.removeGroupFrame(groupId)
                    return
                }

                const left = Math.min(...boxes.map(b => b.left))
                const top = Math.min(...boxes.map(b => b.top))
                const right = Math.max(...boxes.map(b => b.left + b.width))
                const bottom = Math.max(...boxes.map(b => b.top + b.height))

                let frame = this.groupFrameByGroupId.get(groupId)
                if (!frame) {
                    frame = new fabric.Rect({
                        // getBoundingRect() (below) always returns a top-left corner, but Rect
                        // defaults to origin center/center - without this, "left"/"top" set the
                        // frame's CENTER to that point instead, shifting the whole box by half its
                        // own width/height off of where the members actually are.
                        originX: 'left',
                        originY: 'top',
                        fill: 'transparent',
                        // Hidden until selected/hovered (see updateGroupFrameVisibility) via the
                        // stroke color, not object opacity - opacity:0 makes fabric skip rendering
                        // the object entirely, including its hit-testing. No perPixelTargetFind
                        // here, so hit-testing (bounding box only) stays unaffected by stroke color.
                        stroke: 'transparent',
                        strokeWidth: 1.5,
                        strokeDashArray: [6, 4],
                        selectable: true,
                        evented: true,
                        hasControls: false,
                        hasBorders: false,
                        data: { isGroupFrame: true, groupId }
                    })
                    this.groupFrameByGroupId.set(groupId, frame)
                    this.fabricCanvas.add(frame)
                }
                frame.set({ left, top, width: right - left, height: bottom - top })
                frame.setCoords()
                this.fabricCanvas.sendObjectToBack(frame)
            })

            this.updateGroupFrameVisibility()
        },

        removeGroupFrame(groupId) {
            const frame = this.groupFrameByGroupId.get(groupId)
            if (!frame) return
            this.fabricCanvas.remove(frame)
            this.groupFrameByGroupId.delete(groupId)
        },

        onSelectionChanged(e) {
            if (this.suppressSelectionEvents) return
            // e.selected is only the delta this gesture added (selection:updated fires just the
            // newly-added object) - the actual current selection must come from the canvas.
            const activeObjects = this.fabricCanvas.getActiveObjects().filter(o => o.data)
            if (!activeObjects.length) return

            // Clicking a group's frame is the only way to select the whole group - clicking an
            // individual member (even one already in a group) only ever selects that member on
            // its own, driving OverlayConfigPanel for it specifically.
            if (activeObjects.length === 1 && activeObjects[0].data.isGroupFrame) {
                const memberIds = this.overlays.filter(o => o.groupId === activeObjects[0].data.groupId).map(o => o.id)
                this.suppressSelectionEvents = true
                this.selectOverlays(memberIds)
                this.suppressSelectionEvents = false

                this.$emit('select', null)
                const activeObject = this.fabricCanvas.getActiveObject()
                if (activeObject instanceof fabric.ActiveSelection) this.emitMultiSelectionToolbar(activeObject)
                return
            }

            const ids = activeObjects.filter(o => o.data.overlayId).map(o => o.data.overlayId)
            if (!ids.length) return

            if (ids.length === 1) {
                this.$emit('multi-selection', null)
                this.$emit('select', ids[0])
                this.forceSelectionRepaint()
                // Was this replacing a visibly-selected group's frame - e.g. clicking straight
                // from the group onto an unrelated single object - without this the frame's
                // opacity was never told to drop back to 0 and stayed on screen.
                this.updateGroupFrameVisibility()
                return
            }

            this.$emit('select', null)
            const activeObject = this.fabricCanvas.getActiveObject()
            if (activeObject instanceof fabric.ActiveSelection) this.emitMultiSelectionToolbar(activeObject)
            this.updateGroupFrameVisibility()
        },

        // text/hashtag/mention/place all have exactly one piece of freeform text content a user
        // would reasonably want to edit directly on the video instead of hunting for it in the
        // side panel - null for anything else (a shape, an image, datetime/weather/music/gif,
        // which are either not text-driven or computed rather than freely typed).
        editableDataField(overlay) {
            if (overlay.type === 'text') return 'text'
            if (overlay.widgetType === 'hashtag') return 'tag'
            if (overlay.widgetType === 'mention') return 'username'
            if (overlay.widgetType === 'place') return 'location'
            return null
        },

        // Rather than fighting Fabric's own nested-group editing (a Textbox/Text node inside a
        // Group - which every widget pill, and any text with a background/border/icon, actually
        // is - can't just enterEditing() in place the way a bare Textbox can), this hands off to
        // a plain HTML input floated on top of the canvas at the object's own position - see
        // VideoPreviewCanvas.vue's own inline editor. Works identically regardless of whether the
        // object happens to be a lone Textbox or nested three levels deep in a pill's Group.
        onCanvasDoubleClick(e) {
            const obj = e.target
            if (!obj || !obj.data) return
            const overlay = this.overlays.find(o => o.id === obj.data.overlayId)
            if (!overlay) return
            const field = this.editableDataField(overlay)
            if (!field) return

            const canvasRect = this.fabricCanvas.lowerCanvasEl.getBoundingClientRect()
            const scaleX = canvasRect.width / this.canvasSize.width
            const scaleY = canvasRect.height / this.canvasSize.height
            const centerX = canvasRect.left + obj.left * scaleX
            const centerY = canvasRect.top + obj.top * scaleY
            const boxWidth = Math.max(80, obj.getScaledWidth() * scaleX)
            const boxHeight = Math.max(28, obj.getScaledHeight() * scaleY)

            this.$emit('start-edit-text', {
                overlayId: overlay.id,
                field,
                value: (overlay.data && overlay.data[field]) || '',
                style: {
                    left: (centerX - boxWidth / 2) + 'px',
                    top: (centerY - boxHeight / 2) + 'px',
                    width: boxWidth + 'px',
                    height: boxHeight + 'px'
                }
            })
        },

        // Same canvas-to-screen conversion as onCanvasDoubleClick, applied to the ActiveSelection
        // itself. Horizontal centering is left to the CSS (translateX(-50%), see preview.scss).
        emitMultiSelectionToolbar(activeSelection) {
            const memberIds = activeSelection.getObjects().map(o => o.data && o.data.overlayId).filter(Boolean)
            if (memberIds.length < 2) {
                this.$emit('multi-selection', null)
                return
            }

            const canvasRect = this.fabricCanvas.lowerCanvasEl.getBoundingClientRect()
            const scaleX = canvasRect.width / this.canvasSize.width
            const scaleY = canvasRect.height / this.canvasSize.height
            const centerX = canvasRect.left + activeSelection.left * scaleX
            const centerY = canvasRect.top + activeSelection.top * scaleY
            const halfHeight = (activeSelection.getScaledHeight() * scaleY) / 2

            const style = {
                left: centerX + 'px',
                top: (centerY - halfHeight - 44) + 'px'
            }

            const { isCompleteGroup, groupId } = this.isCompleteGroupSelection(memberIds)

            this.$emit('multi-selection', {
                overlayIds: memberIds,
                style,
                isCompleteGroup,
                groupId
            })
        },

        // True when overlayIds is EXACTLY the full membership of one shared, non-null groupId -
        // shared by the floating toolbar's Grouper/Dégrouper label and selectOverlays' own
        // decision to hide fabric's default selection chrome for a complete group.
        isCompleteGroupSelection(overlayIds) {
            const overlaysById = new Map(this.overlays.map(o => [o.id, o]))
            const memberOverlays = overlayIds.map(id => overlaysById.get(id)).filter(Boolean)
            const groupIds = new Set(memberOverlays.map(o => o.groupId).filter(Boolean))
            const soleGroupId = groupIds.size === 1 ? [...groupIds][0] : null
            const isCompleteGroup = !!soleGroupId &&
                memberOverlays.every(o => o.groupId === soleGroupId) &&
                this.overlays.filter(o => o.groupId === soleGroupId).length === overlayIds.length
            return { isCompleteGroup, groupId: isCompleteGroup ? soleGroupId : null }
        },

        onObjectModified(e) {
            const obj = e.target
            if (!obj) return

            // Commits the data-model side only (percentage delta from the selection's left/top
            // plus each member's starting xPct/yPct) - never calls discardActiveObject()/
            // setActiveObject() here, since doing so reentrantly from inside fabric's own
            // end-of-drag dispatch left the rubber-band selection stuck following the cursor.
            if (obj instanceof fabric.ActiveSelection && this.groupDragStart) {
                const deltaXPct = (obj.left - this.groupDragStart.left) / this.canvasSize.width
                const deltaYPct = (obj.top - this.groupDragStart.top) / this.canvasSize.height

                this.groupDragStart.positions.forEach((start, overlayId) => {
                    this.$emit('update:overlay-transform', {
                        overlayId,
                        transform: { xPct: start.xPct + deltaXPct, yPct: start.yPct + deltaYPct, zIndex: 1 }
                    })
                })

                this.groupDragStart = null
                // Repositions the floating toolbar to the group's new spot - safe to call here
                // (unlike discard/reselect above) since it only reads obj, never mutates selection.
                this.emitMultiSelectionToolbar(obj)
                return
            }
            if (obj instanceof fabric.ActiveSelection) {
                this.groupDragStart = null
                return
            }
            if (!obj.data) return

            const transform = {
                xPct: obj.left / this.canvasSize.width,
                yPct: obj.top / this.canvasSize.height,
                rotationDeg: obj.angle || 0,
                zIndex: 1
            }

            // Only an actual resize (lastTransformType set by the object:scaling listener, see
            // mounted()) feeds scale/box back into the data model - a plain move or rotation must
            // leave scale/widthPct/heightPct untouched, otherwise syncObjectTransform's reapply on
            // every sync would snap a previously-resized object back down to scale 1 on next move.
            if (this.lastTransformType === 'scale') {
                transform.scale = obj.scaleX || 1
                transform.widthPct = (obj.width * obj.scaleX) / this.canvasSize.width
                transform.heightPct = (obj.height * obj.scaleY) / this.canvasSize.height
            }

            this.lastTransformType = null
            this.$emit('update:overlay-transform', { overlayId: obj.data.overlayId, transform })
        }
    }
}
</script>

<style lang="scss" scoped></style>
